import { DocumentStatus, Document, Maybe } from "@/graphql/__generated__/graphql";
import { clearDocumentSearchParams } from "@/pages/EditTender/DocumentChat/utils";
import { XIcon, Square, ArrowUpIcon } from "lucide-react";
import { useCallback, RefObject, use, useRef, Dispatch, SetStateAction } from "react";
import { TwCircle } from "../../shared";
import ThreadScrollToBottom from "./ThreadScrollToBottom";
import ThreadWelcomeSuggestions from "./ThreadWelcomeSuggestions";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { DocumentContext, DocumentContextType } from "@/context/DocumentProvider";
import { useParams, useSearchParams } from "react-router-dom";
import { useDocumentChatRuntime } from "@/store/documentChatRuntime";
import { Badge } from "../../ui/badge";
import { useTranslation } from "react-i18next";
import { useTenderPermissionsData } from "@/hooks/cache";
import { cn } from "@/lib/utils";
import { ChatStatus } from "@/constants/enums";

// Constants
const isDevMode = import.meta.env.VITE_APP_ENV === "dev";
interface Props {
  tenderDocumentsData: Document[];
  showDocumentsView: boolean;
  setShowDocumentsView: Dispatch<SetStateAction<boolean>>;
  hasConversation: boolean;
  aiImportId: Maybe<number> | undefined;
  isAtBottom: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Callback to set the pending question when user sends a message */
  onQuestionSent: (_question: string) => void;
  /** Shared send function from useChatSSE hook */
  send: (_params: SendParams) => Promise<void>;
  /** Shared reset function from useChatSSE hook */
  reset: () => void;
}

const Composer = (props: Props) => {

  // Props
  const {
    tenderDocumentsData, showDocumentsView,
    setShowDocumentsView, hasConversation, aiImportId, isAtBottom, scrollRef,
    onQuestionSent, send, reset
  } = props;

  // Refs
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const lastUserMessageRef = useRef<string>(""); // Stores last user message for resend functionality

  // Hooks
  const [searchParams, setSearchParams] = useSearchParams();
  // React hooks for internationalization and routing
  const { t } = useTranslation(["components", "common"]);

  // Real-time streaming state from document chat runtime store
  // Runtime store selectors (additional getters available if needed)
  const streamStatus = useDocumentChatRuntime((s) => s.status); // Connection status (connecting/open/closed)
  const error = useDocumentChatRuntime((s) => s.error); // Any streaming errors

  const { tenderID } = useParams<{ tenderID: string }>();

  const { canUseDocumentChat } = useTenderPermissionsData(Number(tenderID));

  // Derived state from streaming status
  const isStreaming = streamStatus === ChatStatus.CONNECTING || streamStatus === ChatStatus.OPEN;

  // Document context for text selection functionality
  const {
    selectedText,
    setSelectedText,
    setShouldClearSelection,
  } = use(DocumentContext) as DocumentContextType;

  // Filter documents to only show those available in the knowledge base
  const documentsInKnowledgeBase = tenderDocumentsData
    ?.filter((document) => document.status === DocumentStatus.InKnowledgeBase);

  /**
   * Memoized callback for scrolling to bottom.
   * Used by the scroll-to-bottom button.
   */
  const handleScrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [scrollRef]);

  /**
   * Handles sending a user message to the AI chat system.
   * Validates input, manages conversation state, and initiates streaming response.
   * Memoized to prevent unnecessary re-renders in child components.
   *
   * @async
   * @function handleSend
   * @returns {Promise<void>}
   */
  const handleSend = useCallback(async (): Promise<void> => {
    const currentVal = inputRef.current?.value ?? "";
    const trimmed = currentVal.trim();

    // Prevent sending empty messages or multiple concurrent requests
    if (!trimmed || isStreaming) return;

    try {
      // Notify parent that question was sent (will set pendingQuestion and hideDbMessages)
      onQuestionSent(trimmed);
      lastUserMessageRef.current = trimmed; // Store for potential resend

      // Clear input field
      if (inputRef.current) inputRef.current.value = "";

      // Initiate streaming chat request with context
      await send({
        question: trimmed,
        aiImportId: aiImportId ?? undefined,
        selectedContext: selectedText ?? undefined, // Include selected text if any.
      });
    } catch (e) {
      console.error("Failed to send message", e);
    }
  }, [isStreaming, send, aiImportId, selectedText, onQuestionSent]);

  /**
   * Handles clicking on suggested prompt buttons.
   * Immediately sends the suggestion as a user message.
   * Memoized to prevent unnecessary re-renders.
   *
   * @param {string} action - The suggested prompt text to send
   */
  const handleSuggestion = useCallback((action: string): void => {
    // Notify parent that question was sent (will set pendingQuestion and hideDbMessages)
    onQuestionSent(action);
    lastUserMessageRef.current = action;

    send({
      question: action,
      aiImportId: aiImportId ?? undefined,
    });
  }, [send, aiImportId, onQuestionSent]);

  /**
   * Clears the selected text and sets the flag to indicate that the selection should be cleared.
   * Memoized to prevent unnecessary re-renders.
   *
   * @function
   * @name handleClearSelection
   * @returns {void}
   */
  const handleClearSelection = useCallback((): void => {
    setSelectedText(undefined);
    setShouldClearSelection(true);
  }, [setSelectedText, setShouldClearSelection]);

  return (
    <div className="bg-background relative mx-auto pr-1 flex w-full max-w-(--thread-max-width) flex-col gap-4 pl-(--thread-padding-x) pb-4 md:pb-6">
      {hasConversation && <ThreadScrollToBottom disabled={isAtBottom} onClick={handleScrollToBottom} />}
      {!hasConversation && canUseDocumentChat && <ThreadWelcomeSuggestions onSuggestionClick={handleSuggestion} t={t} />}
      <div className="relative flex w-full flex-col rounded-2xl focus-within:ring-2 focus-within:ring-black focus-within:ring-offset-2 dark:focus-within:ring-white">
        <textarea
          placeholder={t("documentchat.chat.placeholder")}
          className={cn(
            "bg-muted border-border dark:border-muted-foreground/15 focus:outline-primary placeholder:text-muted-foreground max-h-[calc(50dvh)] min-h-16 w-full resize-none rounded-t-2xl border-x border-t px-4 pb-3 pt-2 text-base outline-none",
            isStreaming && "opacity-50 cursor-not-allowed"
          )}
          rows={1}
          ref={inputRef}
          disabled={isStreaming || !canUseDocumentChat}
          onFocus={() => {
            // Ensure focusing the textarea never opens the document viewer
            if (!showDocumentsView) {
              clearDocumentSearchParams(searchParams, setSearchParams);
              setShowDocumentsView(false);
            }
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <div className="bg-muted border-border dark:border-muted-foreground/15 relative flex items-center justify-between rounded-b-2xl border-x border-b p-2">
          <div className="flex items-center gap-1 pl-2">
            <Label className="text-muted-foreground text-xs">{t("documentchat.chat.context")}</Label>
            {selectedText ? (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="default" className="cursor-pointer" onClick={handleClearSelection}>
                    <XIcon className="size-4" />
                    {t("documentchat.chat.textselection")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-[400px] line-clamp-20 truncate">
                  {selectedText}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger className="mb-1">
                  <Badge variant="outline">{t("documentchat.chat.alldocuments")}</Badge>
                </TooltipTrigger>
                <TooltipContent className="line-clamp-10 truncate">
                  {documentsInKnowledgeBase?.map((doc) =>
                    <p key={doc.id} className="max-w-[400px] truncate">
                      {doc.fileName}.{doc.format.toLowerCase()}
                    </p>)
                  }
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          <Button
            type="button"
            variant="default"
            className="dark:border-muted-foreground/90 border-muted-foreground/60 hover:bg-primary/75 size-8 rounded-full border"
            aria-label={isStreaming ? "Stop generating" : "Send message"}
            onClick={isStreaming ? () => reset() : handleSend}
            disabled={!canUseDocumentChat}
          >
            {isStreaming ?
              <Square className="size-3.5 fill-white dark:size-4 dark:fill-black" />
              :
              <ArrowUpIcon className="size-5" />
            }
          </Button>
        </div>
      </div>

      {/* Connection statuses - for debugging on Dev/Local */}
      {isDevMode && (
        <div className="flex items-center gap-2">
          <div className="ml-1 -mt-2 flex items-center gap-2">
            <Label className="text-muted-foreground text-xs">Connection</Label>
            <TwCircle colour={streamStatus === ChatStatus.OPEN ? "green" : "red"}/>
          </div>
          <div className="ml-1 -mt-2 flex items-center gap-2">
            <Label className="text-muted-foreground text-xs">Streaming</Label>
            <TwCircle colour={isStreaming ? "green" : "red"}/>
          </div>
          <div className="ml-1 -mt-2 flex items-center gap-2">
            <Label className="text-muted-foreground text-xs">Error</Label>
            <TwCircle colour={error ? "red" : "green"}/>
            {error}
          </div>
        </div>
      )}
    </div>
  );
};

export default Composer;
